import React from "react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from "recharts";

export default function ComparisonView({ data }) {
  const empMap = Object.fromEntries((data.employee_scores || []).map((s) => [s.item_id, s]));
  const mgrMap = Object.fromEntries((data.manager_scores || []).map((s) => [s.item_id, s]));
  const rows = data.items.map((it, i) => ({
    idx: i + 1,
    short: `${it.kind === "goal" ? "G" : "T"}${i + 1}`,
    description: it.description,
    kind: it.kind,
    employee: empMap[it.id]?.score ?? 0,
    manager: mgrMap[it.id]?.score ?? 0,
    gap: Math.abs((empMap[it.id]?.score || 0) - (mgrMap[it.id]?.score || 0)),
    empNotes: empMap[it.id]?.notes || "",
    mgrNotes: mgrMap[it.id]?.notes || "",
  }));
  const avgEmp = (rows.reduce((a, b) => a + b.employee, 0) / rows.length).toFixed(1);
  const avgMgr = (rows.reduce((a, b) => a + b.manager, 0) / rows.length).toFixed(1);
  const avgGap = (rows.reduce((a, b) => a + b.gap, 0) / rows.length).toFixed(1);
  const topGaps = [...rows].sort((a, b) => b.gap - a.gap).slice(0, 3).filter((r) => r.gap > 0);

  return (
    <>
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#00A36C] mb-3">[UNLOCKED] Overlay</div>
      <h1 className="font-display font-bold text-5xl mb-2">{data.title}</h1>
      <p className="text-[#666] mb-10">{data.period_label} · {data.employee_name} · reviewed by {data.manager_name}</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <MetricCard label="Employee avg" value={avgEmp} color="#2B6CB0" testid="metric-emp" />
        <MetricCard label="Manager avg" value={avgMgr} color="#FF4F00" testid="metric-mgr" />
        <MetricCard label="Mean perception gap" value={avgGap} color="#111" testid="metric-gap" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        <div className="cal-card p-6" data-testid="radar-chart">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-4">[ RADAR ] Overlay</div>
          <ResponsiveContainer width="100%" height={360}>
            <RadarChart data={rows}>
              <PolarGrid stroke="#111" strokeOpacity={0.25} />
              <PolarAngleAxis dataKey="short" tick={{ fontSize: 11, fill: "#111", fontFamily: "IBM Plex Mono" }} />
              <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 10, fill: "#666" }} />
              <Radar name="Employee" dataKey="employee" stroke="#2B6CB0" fill="#2B6CB0" fillOpacity={0.35} />
              <Radar name="Manager" dataKey="manager" stroke="#FF4F00" fill="#FF4F00" fillOpacity={0.35} />
              <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11, textTransform: "uppercase" }} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="cal-card p-6" data-testid="bar-chart">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-4">[ BAR ] Per-item</div>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={rows} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid stroke="#111" strokeOpacity={0.1} vertical={false} />
              <XAxis dataKey="short" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#111" />
              <YAxis domain={[0, 5]} tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#111" />
              <Tooltip />
              <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11, textTransform: "uppercase" }} />
              <Bar dataKey="employee" fill="#2B6CB0" name="Employee" />
              <Bar dataKey="manager" fill="#FF4F00" name="Manager" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {topGaps.length > 0 && (
        <div className="mb-10">
          <h2 className="font-display font-bold text-3xl mb-5">Largest perception gaps</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {topGaps.map((g) => (
              <div key={g.idx} className="cal-card cal-shadow p-5" data-testid={`gap-${g.idx}`}>
                <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">{g.kind.toUpperCase()} {String(g.idx).padStart(2, "0")} · Δ{g.gap}</div>
                <div className="mt-2 font-display font-bold text-base leading-snug mb-4">{g.description}</div>
                <div className="flex gap-3 text-xs">
                  <div className="flex-1 border border-[#2B6CB0] p-2"><span className="font-mono-cal text-[#2B6CB0]">EMP</span><div className="font-display font-bold text-2xl">{g.employee}</div></div>
                  <div className="flex-1 border border-[#FF4F00] p-2"><span className="font-mono-cal text-[#FF4F00]">MGR</span><div className="font-display font-bold text-2xl">{g.manager}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="cal-card overflow-hidden" data-testid="detail-table">
        <div className="p-5 border-b border-[#111]">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">Full breakdown</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#111] font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">
                <th className="text-left p-4 w-12">#</th>
                <th className="text-left p-4 w-20">Kind</th>
                <th className="text-left p-4">Description</th>
                <th className="text-center p-4 text-[#2B6CB0]">Emp</th>
                <th className="text-center p-4 text-[#FF4F00]">Mgr</th>
                <th className="text-center p-4">Δ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.idx}>
                  <tr className="border-b border-[#111]/20">
                    <td className="p-4 font-mono-cal text-xs">{String(r.idx).padStart(2, "0")}</td>
                    <td className="p-4 font-mono-cal text-xs">{r.kind.toUpperCase()}</td>
                    <td className="p-4">{r.description}</td>
                    <td className="p-4 text-center font-display font-bold text-xl text-[#2B6CB0]">{r.employee}</td>
                    <td className="p-4 text-center font-display font-bold text-xl text-[#FF4F00]">{r.manager}</td>
                    <td className={`p-4 text-center font-mono-cal ${r.gap >= 2 ? "text-[#FF4F00] font-bold" : "text-[#666]"}`}>{r.gap}</td>
                  </tr>
                  {(r.empNotes || r.mgrNotes) && (
                    <tr className="border-b border-[#111]/10 bg-[#F4F4F0]">
                      <td></td>
                      <td colSpan={5} className="p-3 text-xs text-[#444]">
                        {r.empNotes && <div><span className="font-mono-cal text-[#2B6CB0]">EMP NOTE:</span> {r.empNotes}</div>}
                        {r.mgrNotes && <div className="mt-1"><span className="font-mono-cal text-[#FF4F00]">MGR NOTE:</span> {r.mgrNotes}</div>}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function MetricCard({ label, value, color, testid }) {
  return (
    <div className="cal-card p-6" data-testid={testid}>
      <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">{label}</div>
      <div className="font-display font-bold text-6xl mt-2 tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}
